import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { Position } from '@core/http-types';
import type { InterviewTemplate, InterviewTemplateKind } from '@core/interview-templates-types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
  selectRouteData,
} from '@core/ngrx-legacy';
import { isEmpty } from '@core/utils-legacy';

const { actions, namespace, reducer, selectors } = createEntityCollectionStore<InterviewTemplate>()(
  'InterviewTemplates',
  {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const reducerFunctions = getEntityCollectionActions(
        adapter,
        ({ id }: { id?: number }) => id!,
      );

      return {
        references: (state, payload: { data: InterviewTemplate[] }) => {
          const merged = payload.data.map(template => {
            const existing = state.entityMap[template.id];
            if (!existing) {
              return template;
            }
            const definedFields = <Partial<InterviewTemplate>>(
              Object.fromEntries(Object.entries(template).filter(([, value]) => !isEmpty(value)))
            );
            return { ...existing, ...definedFields };
          });
          return adapter.writeMany(state, merged);
        },

        fetchCollection: (state, payload: { scope: InterviewTemplateKind }) =>
          reducerFunctions.fetchCollection(state, payload),
        fetchCollectionSuccess: (
          state,
          payload: { data: InterviewTemplate[]; scope: InterviewTemplateKind },
        ) => reducerFunctions.fetchCollectionSuccess(state, payload),
        fetchCollectionFailed: (
          state,
          payload: { error: HttpErrorResponse; scope: InterviewTemplateKind },
        ) => reducerFunctions.fetchCollectionFailed(state, payload),

        fetchCandidateCollection: (
          state,
          payload: { candidateId: number; offerId: number; kind: InterviewTemplateKind },
        ) =>
          pipeState(
            state,
            next =>
              reducerFunctions.fetchCollection(next, {
                scope: `${payload.candidateId}-${payload.offerId}-${payload.kind}-stage`,
              }),
            next =>
              reducerFunctions.fetchCollection(next, {
                scope: `${payload.candidateId}-${payload.offerId}-${payload.kind}-offer`,
              }),
            next =>
              reducerFunctions.fetchCollection(next, {
                scope: `${payload.candidateId}-${payload.offerId}-${payload.kind}-other`,
              }),
          ),
        fetchCandidateCollectionSuccess: (
          state,
          payload: {
            stageTemplates: InterviewTemplate[];
            offerTemplates: InterviewTemplate[];
            otherTemplates: InterviewTemplate[];
            candidateId: number;
            offerId: number;
            kind: InterviewTemplateKind;
          },
        ) => {
          const baseScope = `${payload.candidateId}-${payload.offerId}-${payload.kind}`;
          return pipeState(
            state,
            next =>
              reducerFunctions.fetchCollectionSuccess(next, {
                data: payload.stageTemplates,
                scope: `${baseScope}-stage`,
              }),
            next =>
              reducerFunctions.fetchCollectionSuccess(next, {
                data: payload.offerTemplates,
                scope: `${baseScope}-offer`,
              }),
            next =>
              reducerFunctions.fetchCollectionSuccess(next, {
                data: payload.otherTemplates,
                scope: `${baseScope}-other`,
              }),
          );
        },
        fetchCandidateCollectionFailed: (
          state,
          payload: {
            error: HttpErrorResponse;
            candidateId: number;
            offerId: number;
            kind: InterviewTemplateKind;
          },
        ) => {
          const baseScope = `${payload.candidateId}-${payload.offerId}-${payload.kind}`;
          return pipeState(
            state,
            next =>
              reducerFunctions.fetchCollectionFailed(next, {
                error: payload.error,
                scope: `${baseScope}-stage`,
              }),
            next =>
              reducerFunctions.fetchCollectionFailed(next, {
                error: payload.error,
                scope: `${baseScope}-offer`,
              }),
            next =>
              reducerFunctions.fetchCollectionFailed(next, {
                error: payload.error,
                scope: `${baseScope}-other`,
              }),
          );
        },

        updatePositions: (state, payload: { data: Position[] }) =>
          adapter.markAsPending(state, { entities: payload.data.map(({ id }) => id), scope: null }),
        updatePositionsSuccess: (state, payload: { data: InterviewTemplate[] }) =>
          pipeState(
            state,
            next =>
              adapter.markAsLoaded(next, {
                entities: payload.data.map(({ id }) => id!),
                scope: null,
              }),
            next => adapter.writeMany(next, payload.data),
          ),
        updatePositionsFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),
      };
    },
  },
);

const entitiesByKindFromRouter = createSelector(
  selectors.entityMap,
  selectRouteData,
  (entities, data) =>
    Object.values(entities)
      .filter(entity => entity!.kind === data.kind)
      .sort((a, b) => a!.position - b!.position) || [],
);

const mergedSelectors = {
  entitiesByKindFromRouter,
  ...selectors,
};

const InterviewTemplateActions = {
  references: actions.references,

  fetchCollection: actions.fetchCollection,
  fetchCollectionSuccess: actions.fetchCollectionSuccess,
  fetchCollectionFailed: actions.fetchCollectionFailed,

  fetchCandidateCollection: actions.fetchCandidateCollection,
  fetchCandidateCollectionSuccess: actions.fetchCandidateCollectionSuccess,
  fetchCandidateCollectionFailed: actions.fetchCandidateCollectionFailed,

  updatePositions: actions.updatePositions,
  updatePositionsSuccess: actions.updatePositionsSuccess,
  updatePositionsFailed: actions.updatePositionsFailed,
};

export {
  actions,
  InterviewTemplateActions,
  namespace as InterviewTemplateNamespace,
  reducer as InterviewTemplateReducer,
  mergedSelectors as InterviewTemplateSelectors,
};
